import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
import _inheritsLoose from "@babel/runtime/helpers/inheritsLoose";
import _extends from "@babel/runtime/helpers/extends";
var _excluded = ["classes", "className", "disabled", "error", "edsContext", "FormHelperTextProps", "helperText", "id", "InputLabelProps", "InputProps", "inputProps", "label", "multiline", "rows", "minRows", "maxRows", "maxCharacters", "onAdornmentClick", "onEnterPress", "onKeyDown", "onKeyUp", "passwordToggle", "required", "type", "value", "resetWidth", "size", "innerRef"];
import React, { cloneElement, Component } from 'react';
import { default as MuiTextField } from '@mui/material/TextField';
import { default as withStyles } from '../styles';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '../Tooltip';
import Typography from '../Typography';
import InputNumber from './internal/InputNumber';
import { withEDSContext } from '../EDSContext/EDSContext';
import TextFieldLabel from './internal/TextFieldLabel';
import { borderRadiusMedium, colorBorderGlobalFocus, fontSizeDefault, fontSizeSmall, heightInputFieldsSmall, lineHeightHeader2Small, paddingTextField, spacing40, spacing30, spacing80, widthInputFields, borderWidthThin, borderWidthThick, spacing50, boxShadowFocus, boxShadowError } from '../styles/tokens';
import omit from 'lodash/omit';
import merge from 'lodash/merge';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isNil from 'lodash/isNil';
import uuid from 'uuid/v4';

// This object holds all of the props that are specific to our TextField and should not be included for the numeric TextField
var MUI_TEXTFIELD_PROPS = ['classes', 'margin', 'maxCharacters', 'multiline', 'onAdornmentClick', 'onClearClick', 'onEnterPress', 'passwordToggle', 'rows', 'minRows', 'maxRows', 'select', 'SelectProps', 'type', 'edsContext'];
var adornmentStyles = {
  margin: 0,
  padding: spacing40,
  height: 'auto',
  maxHeight: 'none',
  lineHeight: '19px',
  border: 'none',
  backgroundColor: 'transparent',
  borderRadius: '0 0.19rem 0.19rem 0',
  '&:focus': {
    color: colorBorderGlobalFocus,
    boxShadow: "0 0 0 " + borderWidthThick + " " + colorBorderGlobalFocus,
    outline: 0,
    marginRight: '.1rem'
  }
};
var styles = function styles(theme) {
  var _inputSearch;
  return {
    // styles applied to the TextField component
    root: {},
    small: {
      '& $outlinedInputRoot': {
        height: heightInputFieldsSmall
      },
      '& $input, & $input:focus': {
        lineHeight: 'unset',
        padding: spacing30,
        fontSize: fontSizeDefault
      },
      '& $inputLabel': {
        top: 0,
        left: '-0.25rem',
        // -4 px
        lineHeight: 'unset',
        fontSize: fontSizeDefault
      },
      '& $inputLabel[data-shrink=true]': {
        display: 'none'
      }
    },
    // styles applied to the OutlinedInput subcomponent of TextField
    outlinedInputRoot: {
      backgroundColor: theme.palette.grey[100],
      border: borderWidthThin + " solid " + theme.palette.grey[400],
      borderRadius: borderRadiusMedium,
      height: 'auto',
      padding: 0
    },
    standardWidth: {
      width: widthInputFields
    },
    focused: {
      boxShadow: boxShadowFocus
    },
    error: {
      border: borderWidthThick + " solid " + theme.palette.status.error.fill,
      '&.Mui-focused': {
        boxShadow: boxShadowError,
        '-webkit-box-shadow': boxShadowError,
        '-moz-box-shadow:': boxShadowError
      }
    },
    notchedOutline: {
      border: 'none'
    },
    disabled: {
      //this class applies to the OutlinedInput component and the input element
      backgroundColor: theme.palette.grey[200],
      borderRadius: borderRadiusMedium,
      '&:hover': {
        cursor: 'not-allowed'
      }
    },
    multiline: {
      paddingTop: '1.375rem',
      paddingBottom: '.625rem'
    },
    sizeSmall: {},
    adornedStart: {},
    adornedEnd: {},
    // styles applied to the input element
    input: {
      flex: 1,
      height: '1.25rem',
      lineHeight: lineHeightHeader2Small,
      padding: paddingTextField,
      width: 'auto',
      '&::placeholder': {
        color: theme.palette.grey[500],
        fontSize: theme.typography.body1.fontSize,
        opacity: '1'
      },
      '&:placeholder-shown': {
        textOverflow: 'ellipsis'
      },
      '&::-ms-reveal': {
        display: 'none'
      },
      '&::-ms-input-placeholder': {
        color: theme.palette.grey[500],
        fontSize: theme.typography.body1.fontSize,
        opacity: '1'
      }
    },
    inputAdornedStart: {
      paddingLeft: spacing30
    },
    inputMultiline: {
      paddingRight: spacing40,
      paddingLeft: spacing40,
      paddingTop: 0
    },
    inputSearch: (_inputSearch = {
      padding: spacing40
    }, _inputSearch["&[type=\"search\"]::-webkit-search-decoration,\n        &[type=\"search\"]::-webkit-search-cancel-button,\n        &[type=\"search\"]::-webkit-search-results-button,\n        &[type=\"search\"]::-webkit-search-results-decoration"] = {
      display: 'none'
    }, _inputSearch['&[type="search"]'] = {
      '-webkit-appearance': 'none'
    }, _inputSearch['&[type=search]::-ms-clear'] = {
      display: 'none',
      width: 0,
      height: 0
    }, _inputSearch),
    inputSearchWithLabel: {
      padding: paddingTextField
    },
    inputAdornedEnd: {},
    // styles passed to the input label element
    inputLabel: {
      cursor: 'text',
      fontSize: theme.typography.body1.fontSize,
      fontWeight: theme.typography.fontWeightRegular,
      left: 2,
      width: 'calc(100% - 1rem)',
      // needs to be assigned for ellipsis to work, 100% causes horizontal scrollbar to flash when opening
      maxWidth: 'calc(100% - 1rem)'
    },
    inputLabelShrink: {
      fontSize: theme.typography.body2.fontSize,
      lineHeight: 'normal',
      top: '1.0625rem',
      // 17 px - specific for inputs
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    inputLabelAsterisk: {
      position: 'absolute'
    },
    inputLabelWidthForEndAdornment: {
      width: 'calc(100% - 3rem)'
    },
    inputLabelWrapForAdornment: {
      marginRight: spacing80
    },
    // styles applied to adornments
    adornment: _extends({
      fill: theme.palette.grey[500],
      color: theme.palette.grey[500],
      cursor: 'pointer'
    }, adornmentStyles),
    passwordAdornmentRoot: _extends({
      color: theme.palette.grey[500],
      fontSize: theme.typography.caption.fontSize,
      fontWeight: theme.typography.button.fontWeight,
      textDecoration: 'none'
    }, adornmentStyles, {
      '&:hover': {
        color: theme.palette.ctaColor.hover,
        cursor: 'pointer'
      },
      '&:active': {
        color: theme.palette.ctaColor.active
      }
    }),
    adornmentDisabled: {
      fill: theme.palette.grey[400],
      color: theme.palette.grey[400],
      cursor: 'not-allowed',
      outline: 'none',
      '&:hover': {
        cursor: 'not-allowed',
        fill: theme.palette.grey[400],
        color: theme.palette.grey[400]
      },
      '&:active': {
        fill: theme.palette.grey[400],
        color: theme.palette.grey[400]
      }
    },
    adornmentButton: {
      '&:hover': {
        color: theme.palette.ctaColor.hover
      },
      '&:active': {
        color: theme.palette.ctaColor.active
      },
      '&:focus': {
        border: borderWidthThick + " solid " + colorBorderGlobalFocus,
        outline: 'none',
        top: '0.3125rem'
      },
      '&:focus:hover': {
        border: 'none',
        outline: 'none',
        top: '0.375rem'
      },
      '&:disabled': {
        color: theme.palette.ctaColor.disabled
      },
      '&:disabled:hover': {
        backgroundColor: 'transparent',
        color: theme.palette.ctaColor.disabled
      }
    },
    // styles applied to form labels
    inputFormLabel: {
      color: theme.palette.status.error.text + " !important"
    },
    helperTextContainer: {
      display: 'flex'
    },
    helperTextAlignment: {
      justifyContent: 'space-between'
    },
    counterTextAlignment: {
      justifyContent: 'flex-end'
    },
    charCounterContainer: {
      marginLeft: spacing50,
      whiteSpace: 'nowrap'
    },
    formHelperTextRoot: {
      fontFamily: theme.typography.fontFamily,
      fontSize: fontSizeSmall,
      color: theme.palette.text.secondary,
      '&$error': {
        color: theme.palette.status.error.text
      },
      '&$disabled': {
        color: theme.palette.text.disabled
      },
      margin: spacing30 + " 0 0",
      textTransform: 'none',
      letterSpacing: 'unset'
    }
  };
};

/**
 * Use `TextField` to allow users to enter and edit data.
 * @done true
 * @updated v7.6.0
 * @versionAdded v0.0.3
 * @examples
 *  Label
 *  LabelAndPlaceholder
 *  FullWidth
 *  ResponsiveFullWidth
 *  Required
 *  DisabledAndReadonly
 *  Multiline
 *  MultilineLimit
 *  MultilineRequired
 *  Password
 *  Uncontrolled
 *  CharacterCounter
 *  Number
 *  InputAdornments
 */
var TextField = /*#__PURE__*/function (_Component) {
  _inheritsLoose(TextField, _Component);
  function TextField(props) {
    var _this;
    _this = _Component.call(this, props) || this;
    _this.cloneAdornment = function (element, classes) {
      var id = _this.props.id || '';
      var isButtonElement = element.props.component === 'button';
      var adornmentClasses;

      // merge in any incoming adornment classes
      if (element.props.classes) {
        adornmentClasses = merge({
          root: classNames(classes.adornment, element.props.classes.root)
        }, omit(element.props.classes, ['root']));
      } else {
        adornmentClasses = {
          root: classes.adornment
        };
      }
      return /*#__PURE__*/cloneElement(element, {
        classes: adornmentClasses,
        id: id + "_Adornment",
        onClick: isButtonElement && !element.props.onClick ? _this.handleAdornmentClick : element.props.onClick,
        size: _this.props.size
      });
    };
    _this.handleAdornmentClick = function (event, isPasswordVisible) {
      if (_this.props.onAdornmentClick) {
        // Prevents triggering onEnterPress event of TextField when user press enter key on search icon.
        event.preventDefault();
        _this.props.type === 'password' ? _this.props.onAdornmentClick(event, !isPasswordVisible) : _this.props.onAdornmentClick(event);
      }
    };
    _this.handleBlur = function (event) {
      if (_this.props.onBlur) {
        _this.props.onBlur(event);
      }
    };
    _this.handleChange = function (event) {
      if (event.target.value.length > 0) {
        _this.setState({
          count: event.target.value.length
        });
      } else {
        _this.setState({
          count: 0
        });
      }
      if (_this.props.onChange) {
        _this.props.onChange(event);
      }
    };
    _this.handleEnterPress = function (event) {
      var key = event.key;
      if (key === 'Enter') {
        if (_this.props.onEnterPress) {
          _this.props.onEnterPress(event);
        }
      }
    };
    _this.handleFocus = function (event) {
      if (_this.props.onFocus) {
        _this.props.onFocus(event);
      }
    };
    _this.toggleVisibilityClick = function () {
      if (!_this.props.disabled) {
        _this.setState(function (prevState) {
          return {
            isPasswordVisible: !prevState.isPasswordVisible
          };
        });
      }
    };
    _this.helperTextNode = function () {
      var _classNames2;
      var _this$props = _this.props,
        helperText = _this$props.helperText,
        maxCharacters = _this$props.maxCharacters,
        classes = _this$props.classes;
      var charCounterNode;
      var a11yCharCounterNode;
      var count = _this.state.count;

      // if we're dispalaying a character counter
      if (isObject(maxCharacters)) {
        var _classNames;
        // build the counter screen reader text
        var a11yCharCounterText = _this.props.edsContext.formatMessage('component.TextField.characterCounter', {
          count: count,
          total: maxCharacters.max
        });
        var countErrorState = count > maxCharacters.max;

        // build the counter text node
        var nodeText = /*#__PURE__*/React.createElement("span", {
          "aria-hidden": "true",
          className: classNames(classes.charCounterContainer, (_classNames = {}, _classNames[classes.inputFormLabel] = countErrorState, _classNames))
        }, count, " / ", maxCharacters.max);

        // build the final counter node, optionally encasing it in a tooltip
        charCounterNode = maxCharacters.tooltipText ? /*#__PURE__*/React.createElement(Tooltip, {
          title: maxCharacters.tooltipText,
          placement: _this.props.edsContext.dir === 'rtl' ? 'left' : 'right'
        }, nodeText) : nodeText;
        a11yCharCounterNode = /*#__PURE__*/React.createElement(Typography, {
          variant: "srOnly",
          component: "span"
        }, a11yCharCounterText);
      }
      return (helperText || charCounterNode) && /*#__PURE__*/React.createElement("span", {
        className: classNames(classes.helperTextContainer, (_classNames2 = {}, _classNames2[classes.helperTextAlignment] = helperText, _classNames2[classes.counterTextAlignment] = !helperText, _classNames2))
      }, helperText && /*#__PURE__*/React.createElement("span", null, helperText), charCounterNode !== undefined && charCounterNode, a11yCharCounterNode);
    };
    _this.state = {
      isPasswordVisible: false,
      count: _this.props.value && _this.props.value.length ? _this.props.value.length : 0
    };
    return _this;
  }
  var _proto = TextField.prototype;
  _proto.componentDidUpdate = function componentDidUpdate(prevProps) {
    // if (1) this is a controlled component; (2) chracter counts are active; and (3) the value has changed,
    // update the character count
    if (!isNil(this.props.value) && isObject(this.props.maxCharacters) && prevProps.value !== this.props.value) {
      this.setState({
        count: this.props.value ? this.props.value.length : 0
      });
    }
  };
  _proto.render = function render() {
    var _classNames3,
      _classNames4,
      _this2 = this,
      _classNames5,
      _classNames6;
    var _this$props2 = this.props,
      classes = _this$props2.classes,
      classNameProp = _this$props2.className,
      disabled = _this$props2.disabled,
      error = _this$props2.error,
      edsContext = _this$props2.edsContext,
      FormHelperTextPropsProp = _this$props2.FormHelperTextProps,
      helperTextProp = _this$props2.helperText,
      id = _this$props2.id,
      InputLabelPropsProp = _this$props2.InputLabelProps,
      InputPropsProp = _this$props2.InputProps,
      inputPropsProp = _this$props2.inputProps,
      label = _this$props2.label,
      multiline = _this$props2.multiline,
      rows = _this$props2.rows,
      minRows = _this$props2.minRows,
      maxRows = _this$props2.maxRows,
      maxCharacters = _this$props2.maxCharacters,
      onAdornmentClick = _this$props2.onAdornmentClick,
      onEnterPress = _this$props2.onEnterPress,
      onKeyDown = _this$props2.onKeyDown,
      onKeyUp = _this$props2.onKeyUp,
      passwordToggle = _this$props2.passwordToggle,
      required = _this$props2.required,
      type = _this$props2.type,
      value = _this$props2.value,
      resetWidth = _this$props2.resetWidth,
      size = _this$props2.size,
      innerRef = _this$props2.innerRef,
      rest = _objectWithoutPropertiesLoose(_this$props2, _excluded);
    var isPasswordVisible = this.state.isPasswordVisible;
    var cleanedId = id || uuid();
    var numberAriaLabel = '';
    var numberAriaLabelledby = '';

    // HTML input element props
    var inputProps = _extends({}, inputPropsProp);
    if (inputProps && inputProps['aria-label']) {
      numberAriaLabel = inputProps['aria-label'];
    } else {
      numberAriaLabel = null;
    }
    if (inputProps && inputProps['aria-labelledby']) {
      numberAriaLabelledby = inputProps['aria-labelledby'];
    } else {
      numberAriaLabelledby = null;
    }

    // If type number, handle render in InputNumber
    if (type === 'number') {
      return /*#__PURE__*/React.createElement(InputNumber, _extends({}, omit(this.props, MUI_TEXTFIELD_PROPS), {
        // Send all props, except MUI TextField props
        "aria-label": numberAriaLabel // Re-assign to correct React prop name
        ,
        "aria-labelledby": numberAriaLabelledby // Re-assign to correct React prop name
        ,
        id: cleanedId // Assign id to new cleanedId
      }));
    }
    var rootClassName = classNames(classes.root, (_classNames3 = {}, _classNames3[classes.standardWidth] = !this.props.fullWidth && !resetWidth, _classNames3[classes.small] = size === 'small', _classNames3), classNameProp);

    // Password logic
    var passwordAdornment = /*#__PURE__*/React.createElement(InputAdornment, {
      component: "button",
      type: "button",
      disabled: this.props.disabled,
      classes: {
        root: classes.passwordAdornmentRoot
      },
      className: classNames((_classNames4 = {}, _classNames4[classes.adornmentDisabled] = disabled, _classNames4)),
      id: id ? id + "_PasswordToggle" : '',
      onClick: function onClick(event) {
        _this2.toggleVisibilityClick(event);
        _this2.handleAdornmentClick(event, isPasswordVisible);
      },
      position: "end",
      tabIndex: disabled ? '-1' : '0',
      "aria-label": isPasswordVisible ? edsContext.formatMessage('component.TextField.hidePassword') : edsContext.formatMessage('component.TextField.showPassword'),
      "aria-live": "polite"
    }, /*#__PURE__*/React.createElement("span", null, isPasswordVisible ? edsContext.formatMessage('component.TextField.passwordHide') : edsContext.formatMessage('component.TextField.passwordShow')));

    // MUI InputProps logic
    var InputProps = _extends({}, InputPropsProp);
    var startAdornment = InputProps.startAdornment,
      endAdornment = InputProps.endAdornment;

    // Send size prop to startAdornment because our custom TextField styles
    // messes up MUI's default styles for TextField + startAdornments
    if (startAdornment) {
      if (!Array.isArray(startAdornment)) {
        InputProps.startAdornment = /*#__PURE__*/cloneElement(startAdornment, {
          size: size
        });
      }
    }
    if (endAdornment) {
      InputProps.endAdornment = this.cloneAdornment(endAdornment, classes);
    }
    if (type === 'password' && passwordToggle) {
      InputProps.endAdornment = passwordAdornment;
    }
    InputProps.classes = _extends({
      root: classes.outlinedInputRoot,
      multiline: classes.multiline,
      disabled: classes.disabled,
      //this class gets passed down to the input
      focused: classes.focused,
      error: classes.error,
      input: classes.input,
      inputMultiline: classes.inputMultiline,
      inputTypeSearch: classNames(classes.inputSearch, (_classNames5 = {}, _classNames5[classes.inputSearchWithLabel] = !!label, _classNames5)),
      inputAdornedStart: classes.inputAdornedStart,
      notchedOutline: classes.notchedOutline
    }, InputProps.classes);
    InputProps.onBlur = this.handleBlur;
    InputProps.onFocus = this.handleFocus;
    InputProps.onKeyDown = onKeyDown;
    InputProps.onKeyPress = this.handleEnterPress;
    InputProps.onKeyUp = onKeyUp;
    InputProps.onChange = this.handleChange;

    // if the caller asked for a max length, just set it
    if (maxCharacters && isNumber(maxCharacters)) {
      inputProps.maxLength = maxCharacters;
    }

    // if the caller asked for a character counter, only set the max length if overflow is disallowed
    else if (maxCharacters && isObject(maxCharacters) && !maxCharacters.allowOverflow) {
      inputProps.maxLength = maxCharacters.max;
    }

    // MUI InputLabel props
    var InputLabelProps = _extends({}, InputLabelPropsProp);
    InputLabelProps.classes = {
      root: classNames(classes.inputLabel, (_classNames6 = {}, _classNames6[classes.inputLabelWrapForAdornment] = !isNil(InputProps.endAdornment), _classNames6[classes.inputLabelWidthForEndAdornment] = endAdornment || type === 'password', _classNames6)),
      shrink: classes.inputLabelShrink,
      // this ensures the required asterisk is correctly positioned when the label text overflows
      asterisk: classes.inputLabelAsterisk
    };
    InputLabelProps.disabled = disabled;

    // MUI FormHelper props
    var FormHelperTextProps = _extends({}, FormHelperTextPropsProp);
    FormHelperTextProps.classes = {
      root: classes.formHelperTextRoot,
      error: classes.inputFormLabel
    };
    FormHelperTextProps.error = error;

    // Multiline default rows logic
    var noRowsProvided = isNil(rows) && isNil(minRows) && isNil(maxRows);
    var noMinRows = !isNil(maxRows) && isNil(rows) && isNil(minRows);
    var noMaxRows = !isNil(minRows) && isNil(rows) && isNil(maxRows);
    return /*#__PURE__*/React.createElement(MuiTextField, _extends({
      variant: "outlined",
      className: rootClassName,
      disabled: disabled,
      error: error,
      FormHelperTextProps: FormHelperTextProps,
      helperText: this.helperTextNode(),
      id: cleanedId,
      InputProps: InputProps,
      InputLabelProps: InputLabelProps,
      inputProps: inputProps,
      label: label ? /*#__PURE__*/React.createElement(TextFieldLabel, this.props, label) : null,
      multiline: multiline,
      onChange: this.handleChange,
      required: required,
      type: isPasswordVisible ? 'text' : type // use this to tell if you should hard render a 'text' or the props.type
      ,
      value: value,
      size: size,
      rows: noRowsProvided ? 4 : rows // defaults to 4 rows if rows, minRows and maxRows props don't have a value
      ,
      minRows: noMinRows ? Math.min(maxRows, 4) : minRows // defaults to the lesser of maxRows or 4 when only maxRows has a value
      ,
      maxRows: noMaxRows ? Math.max(minRows, 4) : maxRows // defaults to the greater of minRows or 4 when only minRows has a value
      ,
      ref: innerRef
    }, rest));
  };
  return TextField;
}(Component);
TextField.propTypes = {
  /**
   * This property helps users to fill forms faster, especially on mobile devices.
   * The name can be confusing, as it's more like an autofill.
   * You can learn more about it here:
   * https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill
   */
  autoComplete: PropTypes.string,
  /**
   * If `true`, the input will be focused during the first mount.
   */
  autoFocus: PropTypes.bool,
  /**
   * @ignore
   */
  children: PropTypes.node,
  /**
   * @ignore
   */
  classes: PropTypes.object,
  /**
   * @ignore
   */
  className: PropTypes.string,
  /**
   * The default value of the `Input` element.
   */
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * If `true`, the input will be disabled.
   */
  disabled: PropTypes.bool,
  /**
   * If `true`, the label will be displayed in an error state.
   */
  error: PropTypes.bool,
  /**
   * Properties applied to the `FormHelperText` element.
   */
  FormHelperTextProps: PropTypes.object,
  /**
   * If `true`, the input will take up the full width of its container.
   */
  fullWidth: PropTypes.bool,
  /**
   * The helper text content.
   */
  helperText: PropTypes.node,
  /**
   * The id of the `input` element.
   * Use that property to make `label` and `helperText` accessible for screen readers.
   */
  id: PropTypes.string,
  /**
   * Properties applied to the `InputAdornment` element.
   */
  InputAdornment: PropTypes.object,
  /**
   * Properties applied to the `InputLabel` element.
   */
  InputLabelProps: PropTypes.object,
  /**
   * Properties applied to the `Input` element.
   *
   * When `type='number'`, adornments will not render.
   */
  InputProps: PropTypes.object,
  /**
   * Properties applied to the native `input` element.
   */
  inputProps: PropTypes.object,
  /**
   * Use that property to pass a ref callback to the native input component.
   */
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  /**
   * @ignore
   */
  edsContext: PropTypes.object,
  /**
   * @ignore
   */
  innerRef: PropTypes.func,
  /**
   * The label content.
   */
  label: PropTypes.node,
  /**
   * If `dense` or `normal`, will adjust vertical spacing of this and contained components.
   */
  margin: PropTypes.oneOf(['none', 'dense', 'normal']),
  /**
   * The maximum number of characters that can be entered into the TextField. There are two variants.
   *
   * With Counter: Textbox or Textarea can have counter at the bottom of the field.  allowOverflow: true will allow more that number of specified characters but counter text will be in red color.
   *
   * e.g.
   * - `{ max: number, allowOverflow: boolean, [tooltipText: string]  }`
   *
   * Without Counter: Will not display counter but number of character restriction will be applied.
   *
   * e.g.
   * - `{number}`
   *
   * **Does not apply to number fields (`type="number"`)**
   */
  maxCharacters: PropTypes.oneOfType([PropTypes.number, PropTypes.shape({
    max: PropTypes.number.isRequired,
    allowOverflow: PropTypes.bool
  })]),
  /**
   * If `true`, a textarea element will be rendered instead of an input.
   */
  multiline: PropTypes.bool,
  /**
   * Name attribute of the `input` element.
   */
  name: PropTypes.string,
  /**
   * Callback fired when an [`InputAdornment`](#/components/InputAdornment) is clicked.
   * The value is passed into the provided function.
   *
   * **Note**: This is applicable for end adornments when the element is rendered as a `button`.
   *
   * This function will also be called when `type='password'` and the "Hide/Show" adornment is clicked.
   *
   * @param {object} event The event source of the callback.
   *
   * @param {bool} isPasswordVisible `true` when the password is currently visible and `false` when hidden. This is only provided when `type="password"`
   *
   */
  onAdornmentClick: PropTypes.func,
  /**
   * @ignore
   */
  onBlur: PropTypes.func,
  /**
   * Callback fired when the value is changed.
   *
   * @param {object} event The event source of the callback.
   * You can pull out the new value by accessing `event.target.value`.
   */
  onChange: PropTypes.func,
  /**
   * Callback fired when the enter key is pressed.
   *
   * @param {object} event The event source of the callback.
   * You can pull out the new value by accessing `event.target.value`.
   */
  onEnterPress: PropTypes.func,
  /**
   * Callback fired when the a key is pressed.
   *
   * @param {object} event The event source of the callback.
   * You can pull out the key by accessing `event.key`.
   */
  onKeyDown: PropTypes.func,
  /**
   * Callback fired when the a key is pressed.
   *
   * @param {object} event The event source of the callback.
   * You can pull out the key by accessing `event.key`.
   */
  onKeyUp: PropTypes.func,
  /**
   * @ignore
   */
  onFocus: PropTypes.func,
  /**
   * Set to true to render the Show/Hide text for password TextFields. This will only affect TextField components with the type="password".
   */
  passwordToggle: PropTypes.bool,
  /**
   * The short hint displayed in the input before the user enters a value.
   */
  placeholder: PropTypes.string,
  /**
   * If `true`, the label is displayed as required.
   */
  required: PropTypes.bool,
  /**
   * @ignore
   *
   * internal prop to reset standard width of TextField
   */
  resetWidth: PropTypes.bool,
  /**
   * Static number of rows to display when multiline option is set to true.
   * Defaults to a value of 4 if `rows`, `minRows`, and `maxRows` have no value.
   */
  rows: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Minimum number of rows to display when multiline option is set to true.
   * Defaults to the lesser of `maxRows` or 4 when only `maxRows` has a value.
   */
  minRows: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Maximum number of rows to display when multiline option is set to true.
   * Defaults to the greater of `minRows` or 4 when only `minRows` has a value.
   */
  maxRows: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Render a `Select` element while passing the `Input` element to `Select` as `input` parameter.
   * If this option is set you must pass the options of the select as children.
   */
  select: PropTypes.bool,
  /**
   * Properties applied to the `Select` element.
   */
  SelectProps: PropTypes.object,
  /**
   * Type attribute of the `Input` element. It should be a valid HTML5 input type.
   */
  type: PropTypes.string,
  /**
   * The value of the `Input` element, required for a controlled component.
   */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  /**
   * The size of the `TextField`. Small size is recommended to be used within the `TableEditableCell` component.
   */
  size: PropTypes.oneOf(['small', 'medium']),
  // InputNumber specific
  /**
   * **Only valid for number fields (`type="number"`)**
   *
   * Specifies the valid number intervals
   */
  step: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * **Only valid for number fields (`type="number"`)**
   *
   * Specifies the minimum value. If a value less than the minimum is entered, the field will reset to the
   * minimum value.
   */
  min: PropTypes.number,
  /**
   * **Only valid for number fields (`type="number"`)**
   *
   * Specifies the maximum value. If a value larger than the maximum is entered, the field will reset to the
   * maximum value.
   */
  max: PropTypes.number,
  /**
   * **Only valid for number fields (`type="number"`)**
   *
   * Specifies the precision length of value e.g. `precision={2} => 0.00`
   */
  precision: PropTypes.number,
  /**
   * @ignore
   * **Only valid for number fields (`type="number"`)**
   *
   * Whether focus input when click up or down button
   */
  focusOnUpDown: PropTypes.bool,
  /**
   * @ignore
   * **Only valid for number fields (`type="number"`)**
   *
   * Specifies the format of the value presented
   * `(@param: value: number|string, @param displayValue: string)`
   */
  formatter: PropTypes.func,
  /**
   * @ignore
   * **Only valid for number fields (`type="number"`)**
   *
   * Specifies a regex pattern to be added to the input number element - useful for forcing iOS to open the number pad instead of the normal keyboard (supply a regex of "\d*" to do this) or form validation
   */
  pattern: PropTypes.string,
  /**
   * @ignore
   * **Only valid for number fields (`type="number"`)**
   *
   * Specifies the decimal separator
   */
  decimalSeparator: PropTypes.string,
  /**
  * The system prop that allows defining system overrides as well as additional CSS styles.
  */
  sx: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])), PropTypes.func, PropTypes.object])
};
TextField.defaultProps = {
  required: false,
  type: 'text',
  size: 'medium',
  fullWidth: false,
  step: 1,
  margin: 'none'
};
var TextFieldInternal = withEDSContext(withStyles(styles)(TextField));
TextField.displayName = 'TextField';
TextField.muiName = 'TextField';
export default /*#__PURE__*/React.forwardRef(function WrappedTextFiled(props, ref) {
  return /*#__PURE__*/React.createElement(TextFieldInternal, _extends({}, props, {
    innerRef: ref
  }));
});